"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Pagination, PaginationContent, PaginationEllipsis, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from "@/components/ui/pagination";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const categories = [{
  value: "all",
  label: "ALL"
}, {
  value: "humanity",
  label: "HUMANITY"
}, {
  value: "environment",
  label: "ENVIRONMENT"
}, {
  value: "disease",
  label: "DISEASE"
}, {
  value: "animal",
  label: "ANIMAL"
}];
function formatDate(dateString?: string) {
  if (!dateString) return "";
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit"
    }).toUpperCase();
  } catch {
    return "";
  }
}
function stripHtml(html?: string) {
  if (!html) return "";
  return html.replace(/<[^>]*>/g, "").trim();
}
function BrewGuidesConsumer() {
  const {
    posts,
    loading,
    error,
    refetch,
    currentPage,
    totalPages,
    hasNext,
    hasPrev,
    nextPage,
    prevPage,
    setPage
  } = usePosts() as ReturnType<typeof usePosts> & {
    currentPage: number;
  };
  if (loading) {
    return <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
        {[...Array(6)].map((_, i) => <div key={i} data-index={i} className="border-[3px] border-green-900 bg-green-50 p-0 animate-pulse" style={{
        boxShadow: "6px 6px 0 var(--border)"
      }}>
            <div className="aspect-[4/3] bg-green-100" />
            <div className="p-6 space-y-3">
              <div className="h-3 w-24 bg-green-100" />
              <div className="h-6 w-3/4 bg-green-100" />
              <div className="h-4 w-full bg-green-100" />
              <div className="h-4 w-2/3 bg-green-100" />
            </div>
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="border-[3px] border-green-900 bg-green-50 p-8 text-center">
        <p className="font-default text-base text-green-900 mb-4">
          Failed to load awareness causes.
        </p>
        <Button onClick={refetch} className="bg-green-600 text-white border-[3px] border-green-900 font-default uppercase tracking-wide rounded-lg" style={{
        boxShadow: "6px 6px 0 var(--border)"
      }}>
          Retry
        </Button>
      </div>;
  }
  if (!posts || posts.length === 0) {
    return <div className="border-[3px] border-green-900 bg-green-50 p-12 text-center">
        <p className="font-default text-lg text-green-900">
          No causes published yet. Check back soon.
        </p>
      </div>;
  }
  return <>
      <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
        {posts.map((post, i) => {
        const categoryName = post.categories && post.categories.length > 0 ? post.categories[0].name.toUpperCase() : "IMPACT";
        const excerpt = stripHtml(post.excerpt).slice(0, 140);
        return <article key={post.id} data-index={i} className="group border-[3px] border-green-900 bg-green-50 flex flex-col transition-transform duration-150 ease-out hover:translate-x-1 hover:translate-y-1" style={{
          boxShadow: "6px 6px 0 var(--border)"
        }} onMouseEnter={e => {
          e.currentTarget.style.boxShadow = "2px 2px 0 var(--border)";
        }} onMouseLeave={e => {
          e.currentTarget.style.boxShadow = "6px 6px 0 var(--border)";
        }}>
              <div className="relative border-b-[3px] border-green-900 overflow-hidden">
                <div className="aspect-[4/3] overflow-hidden bg-green-100 relative">
                  {post.featuredImage ? <img src={post.featuredImage} alt={post.title} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-[400ms] ease-out group-hover:scale-[1.03]" /> : <div className="w-full h-full bg-green-200" />}
                  <div className="absolute inset-0 pointer-events-none" style={{
                backgroundImage: "radial-gradient(circle, var(--border) 1px, transparent 1.5px)",
                backgroundSize: "12px 12px",
                opacity: 0.08
              }} />
                </div>

                <div className="absolute top-3 left-3">
                  <div className="bg-green-200 border-[3px] border-green-900 px-3 py-1.5" style={{
                clipPath: "polygon(0% 20%, 8% 0%, 22% 12%, 38% 0%, 52% 14%, 68% 0%, 82% 12%, 100% 20%, 92% 50%, 100% 80%, 82% 70%, 68% 100%, 52% 84%, 38% 100%, 22% 70%, 8% 100%, 0% 80%, 10% 50%)"
              }}>
                    <span className="font-default text-[10px] font-semibold uppercase tracking-[0.18em] text-green-900">
                      {categoryName}
                    </span>
                  </div>
                </div>
              </div>

              <div className="p-6 flex flex-col flex-1 gap-3">
                <span className="font-default text-xs font-medium uppercase tracking-[0.18em] text-green-800">
                  {formatDate(post.date)}
                </span>

                <h3 className="font-default text-xl md:text-2xl font-bold uppercase leading-[1.0] tracking-tight text-green-950">
                  {post.title}
                </h3>

                {excerpt && <p className="font-default text-sm text-green-800 leading-relaxed flex-1">
                    {excerpt}
                    {excerpt.length >= 140 ? "..." : ""}
                  </p>}

                <Link to={post.link || "#"} className="inline-flex items-center gap-1.5 font-default text-sm font-bold uppercase tracking-wide text-green-900 underline underline-offset-4 decoration-2 hover:text-green-600 transition-colors mt-2">
                  SUPPORT
                  <ArrowRight className="size-4" />
                </Link>
              </div>
            </article>;
      })}
      </div>

      {totalPages > 1 && <div className="mt-16 flex justify-center">
          <Pagination>
            <PaginationContent className="gap-2">
              <PaginationItem>
                <Button variant="ghost" onClick={prevPage} disabled={!hasPrev} className="border-[3px] border-green-900 bg-green-50 text-green-900 font-default uppercase tracking-wide rounded-lg disabled:opacity-40 hover:bg-green-100 hover:translate-x-0.5 hover:translate-y-0.5 transition-transform" style={{
              boxShadow: "4px 4px 0 var(--border)"
            }}>
                  PREV
                </Button>
              </PaginationItem>

              {Array.from({
            length: Math.min(totalPages, 5)
          }, (_, idx) => {
            const pageNum = idx + 1;
            const isActive = pageNum === currentPage;
            return <PaginationItem key={pageNum}>
                    <Button variant="ghost" onClick={() => setPage(pageNum)} className={`size-10 p-0 border-[3px] border-green-900 font-default font-bold rounded-lg transition-transform hover:translate-x-0.5 hover:translate-y-0.5 ${isActive ? "bg-green-200 text-green-950" : "bg-green-50 text-green-900 hover:bg-green-100"}`} style={{
                boxShadow: "4px 4px 0 var(--border)"
              }}>
                      {pageNum}
                    </Button>
                  </PaginationItem>;
          })}

              {totalPages > 5 && <PaginationItem>
                  <PaginationEllipsis className="text-green-900" />
                </PaginationItem>}

              <PaginationItem>
                <Button variant="ghost" onClick={nextPage} disabled={!hasNext} className="border-[3px] border-green-900 bg-green-50 text-green-900 font-default uppercase tracking-wide rounded-lg disabled:opacity-40 hover:bg-green-100 hover:translate-x-0.5 hover:translate-y-0.5 transition-transform" style={{
              boxShadow: "4px 4px 0 var(--border)"
            }}>
                  NEXT
                </Button>
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>}
    </>;
}
export default function DiseaseAwarenessGrid() {
  const wp_query_params = {
    post_type: "post",
    per_page: 9,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="1537" id="diseaseawarenessgrid" className="bg-stone-50 text-green-950 border-y-[4px] border-green-900 py-24 md:py-32 px-5 md:px-8 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none" style={{
      backgroundImage: "radial-gradient(circle, var(--foreground) 1px, transparent 1.5px)",
      backgroundSize: "14px 14px",
      opacity: 0.04
    }} />

      <div className="max-w-[1440px] mx-auto relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 mb-16 md:mb-20">
          <div className="lg:col-span-9">
            <div className="flex items-center gap-3 mb-6">
              <span className="font-default text-xs md:text-sm font-medium uppercase tracking-[0.18em] text-green-800">
                MISSION 01
              </span>
              <span className="size-1.5 bg-green-900" />
              <span className="font-default text-xs md:text-sm font-medium uppercase tracking-[0.18em] text-green-800">
                CURRENT CAUSES
              </span>
            </div>

            <h2 className="font-default text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold uppercase leading-[0.92] tracking-tighter text-green-950">
              KEEPING{" "}
              <span className="inline-block bg-green-200 text-green-950 border-[3px] border-green-900 px-3 md:px-5 py-0 md:py-1" style={{
              transform: "rotate(-1.5deg)",
              boxShadow: "6px 6px 0 var(--border)"
            }}>
                AWARENESS
              </span>{" "}
              FRESH
            </h2>

            <p className="font-default text-base md:text-lg text-green-800 mt-6 max-w-2xl leading-relaxed">
              Explore our latest humanitarian initiatives and support the causes that matter most to our community.
            </p>
          </div>

          <div className="lg:col-span-3 flex lg:items-end lg:justify-end">
            <ToggleGroup type="single" defaultValue="all" className="flex flex-wrap gap-2 rounded-lg">
              {categories.map((cat, i) => <ToggleGroupItem key={cat.value} data-index={i} value={cat.value} className="border-[2px] border-green-900 bg-green-50 text-green-900 font-default text-xs font-semibold uppercase tracking-[0.15em] px-3 py-2 rounded-lg data-[state=on]:bg-green-200 data-[state=on]:text-green-950 hover:bg-green-100 transition-colors">
                  {cat.label}
                </ToggleGroupItem>)}
            </ToggleGroup>
          </div>
        </div>

        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 9,
        order: "desc",
        orderby: "date"
      }}>
          <BrewGuidesConsumer />
        </PostsProvider>
      </div>
    </section>;
}